import { useCallback, useRef } from 'react';
import RecordRTC from 'recordrtc';

import { getSeekableBlob } from 'recording/hooks/use-media-recorder';

// Keeps per-turn webm uploads small — the S3 objects are merged/transcoded
// server-side, so per-turn quality doesn't need to match video practice.
const VIDEO_BITS_PER_SECOND = 1_000_000;

const RECORDER_SETTINGS: RecordRTC.Options = {
  type: 'video',
  disableLogs: true,
  mimeType: 'video/webm;codecs=vp9',
  videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
};

export type VideoTurn = {
  blob: Blob;
  durationSeconds: number;
};

type UseVideoTurnRecorderReturn = {
  // Starts recording the given stream for one turn. A no-op if a turn is
  // already being recorded.
  startTurn: (stream: MediaStream) => void;
  // Stops the in-flight turn recording, resolving with the webm blob and its
  // duration. Resolves null if no turn was being recorded or the blob is empty.
  stopTurn: () => Promise<VideoTurn | null>;
  // Discards any in-flight recording without producing a blob.
  cancelTurn: () => void;
};

/**
 * Records a MediaStream to a webm blob, one conversational turn at a time.
 * Video role plays run one instance for the learner's camera+mic and one for
 * the avatar's LiveKit video+audio; each turn's blob is uploaded to S3 in the
 * background (see use-video-turn-uploader) keyed by the turn_id the server
 * hands back on turn_complete.
 */
const useVideoTurnRecorder = (): UseVideoTurnRecorderReturn => {
  const recorderRef = useRef<RecordRTC | null>(null);
  const turnStartedAtRef = useRef<number | null>(null);

  const startTurn = useCallback((stream: MediaStream) => {
    if (recorderRef.current) return;

    const recorder = new RecordRTC(stream, RECORDER_SETTINGS);
    recorder.startRecording();
    recorderRef.current = recorder;
    turnStartedAtRef.current = Date.now();
  }, []);

  const stopTurn = useCallback((): Promise<VideoTurn | null> => {
    const recorder = recorderRef.current;
    const startedAt = turnStartedAtRef.current;
    recorderRef.current = null;
    turnStartedAtRef.current = null;

    if (!recorder) return Promise.resolve(null);

    return new Promise<VideoTurn | null>((resolve) => {
      recorder.stopRecording(async () => {
        const rawBlob = recorder.getBlob();
        recorder.destroy();

        if (!rawBlob?.size) {
          resolve(null);
          return;
        }

        // RecordRTC webms aren't seekable/duration-tagged — fall back to the raw
        // blob if the ts-ebml rewrite fails (see use-media-recorder).
        const seekableBlob = await getSeekableBlob(rawBlob);
        resolve({
          blob: seekableBlob ?? rawBlob,
          durationSeconds: startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0,
        });
      });
    });
  }, []);

  const cancelTurn = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    turnStartedAtRef.current = null;
    if (!recorder) return;

    recorder.stopRecording(() => {
      recorder.destroy();
    });
  }, []);

  return {
    startTurn,
    stopTurn,
    cancelTurn,
  };
};

export default useVideoTurnRecorder;
